// Shell-level toast that renders the front-of-queue fresh achievement.
//
// One toast visible at a time; auto-dismiss after ~6s; click anywhere or press
// Esc to dismiss early. The `user_first_login_ever` row is suppressed here so
// `FirstLoginWelcomeBanner` is the single celebration surface for that key.
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { AchievementBadge } from "@/components/common/kpi/AchievementBadge";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";

const TOAST_TTL_MS = 6000;
const SUPPRESSED_KEYS_FOR_TOAST = new Set<string>(["user_first_login_ever"]);

export function FreshAchievementsToastHost() {
  const ctx = useFreshAchievementsOptional();

  const current = useMemo(() => {
    if (!ctx) return null;
    for (const item of ctx.queue) {
      if (!SUPPRESSED_KEYS_FOR_TOAST.has(item.achievement_key)) return item;
    }
    return null;
  }, [ctx]);

  useEffect(() => {
    if (!ctx || !current) return;
    const timer = window.setTimeout(() => {
      ctx.dismiss(current.id);
    }, TOAST_TTL_MS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") ctx.dismiss(current.id);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctx, current]);

  if (!ctx || !current) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-6 left-1/2 z-[9300] -translate-x-1/2"
    >
      <button
        type="button"
        onClick={() => ctx.dismiss(current.id)}
        className="pointer-events-auto flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50/95 px-3 py-2 text-sm text-amber-950 shadow-xl backdrop-blur-sm transition hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 dark:border-amber-800 dark:bg-amber-950/70 dark:text-amber-100"
      >
        <span className="text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-200">
          Achievement
        </span>
        <AchievementBadge achievementKey={current.achievement_key} metadata={current.metadata} fresh />
      </button>
    </div>,
    document.body,
  );
}
